import { clearParentFinancialAidPointerStorageFrom } from '../financial-aid/parentFinancialAidStorage';
import { disableGoogleIdentityAutoSelect } from './googleIdentityServices';

const LOGOUT_TRANSITION_STORAGE_KEY = 'ystnj.logout.transition.v1';
const LOGOUT_TRANSITION_TTL_MS = 120_000;

export const POST_LOGOUT_PUBLIC_PATH = '/apply?logout=1';

type LogoutTransitionRecord = {
  target: string;
  startedAt: number;
};

const parseRecord = (value: string | null): LogoutTransitionRecord | null => {
  if (!value) {
    return null;
  }

  try {
    const parsed = JSON.parse(value) as Partial<LogoutTransitionRecord>;
    const target = String(parsed.target ?? '').trim() || POST_LOGOUT_PUBLIC_PATH;
    const startedAt = Number(parsed.startedAt ?? 0);
    if (!Number.isFinite(startedAt) || startedAt <= 0) {
      return null;
    }
    return { target, startedAt };
  } catch {
    return null;
  }
};

const readStorage = (): Storage | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

const collectStorageKeys = (storage: Storage): string[] => {
  const keys: string[] = [];
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (key) {
      keys.push(key);
    }
  }
  return keys;
};

export const readLogoutTransition = (): LogoutTransitionRecord | null => {
  const storage = readStorage();
  if (!storage) {
    return null;
  }

  const record = parseRecord(storage.getItem(LOGOUT_TRANSITION_STORAGE_KEY));
  if (!record) {
    return null;
  }

  if (Date.now() - record.startedAt > LOGOUT_TRANSITION_TTL_MS) {
    storage.removeItem(LOGOUT_TRANSITION_STORAGE_KEY);
    return null;
  }

  return record;
};

export const beginLogoutTransition = (target = POST_LOGOUT_PUBLIC_PATH): string => {
  const resolvedTarget = String(target || POST_LOGOUT_PUBLIC_PATH).trim() || POST_LOGOUT_PUBLIC_PATH;
  const storage = readStorage();
  storage?.setItem(
    LOGOUT_TRANSITION_STORAGE_KEY,
    JSON.stringify({
      target: resolvedTarget,
      startedAt: Date.now(),
    } satisfies LogoutTransitionRecord),
  );
  return resolvedTarget;
};

export const clearLogoutTransition = (): void => {
  const storage = readStorage();
  storage?.removeItem(LOGOUT_TRANSITION_STORAGE_KEY);
};

export const resolveLogoutTransitionTarget = (): string | null =>
  readLogoutTransition()?.target ?? null;

export const clearLocalAuthState = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  disableGoogleIdentityAutoSelect();

  const clearTokenKeys = (storage: Storage) => {
    for (const key of collectStorageKeys(storage)) {
      if (
        key.includes('CognitoIdentityServiceProvider')
        || key.endsWith('.idToken')
        || key.endsWith('.accessToken')
        || key.endsWith('.refreshToken')
        || key.endsWith('.signInDetails')
        || key.endsWith('.clockDrift')
      ) {
        storage.removeItem(key);
      }
    }

    storage.removeItem('triedge_persona');
    storage.removeItem('ystnj_persona');
  };

  try {
    clearTokenKeys(window.localStorage);
    clearTokenKeys(window.sessionStorage);
    clearParentFinancialAidPointerStorageFrom(window.localStorage);
    clearParentFinancialAidPointerStorageFrom(window.sessionStorage);
  } catch (error) {
    console.warn('[auth/logoutTransition] Failed to clear local auth state during logout.', error);
  }
};
